import { CallStateEnum } from '@models/call-state.model.ts';
import { SnackbarType } from '@models/app-snackbar.model.ts';
import useSnackbarStore from '@store/snackbar-store/snackbar.store.ts';
import axios from 'axios';
import {
  deleteMedia,
  deleteMediaByPath,
  getMediaLibraryMedia,
  renameMedia
} from '@api/media-library-api.ts';
import { MediaLibraryActionParams } from '@store/media-library-store/media-library.model.ts';
import { MediaModel } from '@models/media-library.model.ts';

const { showSnackbar } = useSnackbarStore.getState();

export const mediaSlice = (set: any, get: any) => ({
  refreshCurrentFolder: () => {
    const currentFolderId = get().currentFolder.id;
    const currentFolderTitle = get().currentFolder.title;

    get().getMediaLibraryFolders(currentFolderId, currentFolderTitle);
    get().getMedia(currentFolderId);
  },

  getMedia: async (id?: string) => {
    set({ mediaCallState: CallStateEnum.Loading });

    const { currentFolder, pagination, sort, filter, search } = get();
    const hasSearchValue = Object.values(search || {}).some(
      (value) => value !== '' && value !== null && value !== undefined
    );
    const folderId = hasSearchValue ? null : id || currentFolder?.id || 'root';

    try {
      const params: any = {
        query: {
          page: pagination.page,
          limit: pagination.rowsPerPage,
          filter: {}
        }
      };

      if (hasSearchValue) {
        params.query.search = {};
        Object.entries(search).forEach(([key, value]) => {
          if (value !== '' && value !== null && value !== undefined) {
            params.query.search[key] = value;
          }
        });
      }

      if (filter) {
        const filteredFilter: Record<string, string> = {};
        Object.entries(filter).forEach(([key, values]) => {
          if (Array.isArray(values) && values.length > 0) {
            filteredFilter[key] = values.join(',');
          }
        });

        if (Object.keys(filteredFilter).length > 0) {
          params.query.filter = filteredFilter;
        }
      }

      if (sort && sort[0]) params.query.sort = `${sort[0]},${sort[1]}`;

      const response = await getMediaLibraryMedia(folderId, params);

      const data = response.rows;
      const totalPages =
        Math.ceil(response.total / pagination.rowsPerPage) || 1;

      let currentPage = pagination.page;
      const prevFolderId = get().currentFolder?.id;
      if (folderId !== prevFolderId || currentPage > totalPages) {
        currentPage = 1;
      }

      const checkedMediaItems: Record<string, boolean> = {};
      data.forEach((media: any) => (checkedMediaItems[media._id] = false));

      set({
        mediaData: data,
        checkedMediaItems,
        pagination: { ...pagination, totalPages, page: currentPage },
        mediaCallState: CallStateEnum.Loaded
      });
    } catch (error: any) {
      set({ mediaCallState: CallStateEnum.Error });
      if (axios.isAxiosError(error))
        showSnackbar(error.response?.data?.message, SnackbarType.Error);
    }
  },

  renameMedia: async ({ data, id, handleClose }: MediaLibraryActionParams) => {
    set({ folderCallState: CallStateEnum.Loading });
    try {
      await renameMedia(data, id as any);
      set({ folderCallState: CallStateEnum.Loaded });
      handleClose?.();
      get().refreshCurrentFolder();
      showSnackbar('snackbar_update_media_text', SnackbarType.Success);
    } catch (error: any) {
      set({ folderCallState: CallStateEnum.Error });
      if (axios.isAxiosError(error))
        showSnackbar(error.response?.data?.message, SnackbarType.Error);
    }
  },

  deleteMedia: async ({ ids, handleClose }: MediaLibraryActionParams) => {
    set({ folderCallState: CallStateEnum.Loading });
    try {
      await deleteMedia(ids as any);
      set({ folderCallState: CallStateEnum.Loaded });
      handleClose?.();
      get().refreshCurrentFolder();
      showSnackbar('snackbar_delete_media_text', SnackbarType.Success);
    } catch (error: any) {
      set({ folderCallState: CallStateEnum.Error });
      if (axios.isAxiosError(error))
        showSnackbar(error.response?.data?.message, SnackbarType.Error);
    }
  },

  deleteSingleMedia: async ({
    data,
    handleClose
  }: MediaLibraryActionParams) => {
    set({ folderCallState: CallStateEnum.Loading });
    try {
      await deleteMediaByPath(data);
      set({ folderCallState: CallStateEnum.Loaded });
      handleClose?.();
      get().refreshCurrentFolder();
      showSnackbar('snackbar_delete_media_text', SnackbarType.Success);
    } catch (error: any) {
      set({ folderCallState: CallStateEnum.Error });
      if (axios.isAxiosError(error)) {
        showSnackbar(error.response?.data?.message, SnackbarType.Error);
      }
    }
  },

  toggleMediaChecked: (id: string, checked: boolean) =>
    set((state: any) => ({
      checkedMediaItems: { ...state.checkedMediaItems, [id]: checked }
    })),

  toggleSelectAllMedia: (checked: boolean) => {
    const allChecked: Record<string, boolean> = {};
    get().mediaData.forEach((media: any) => (allChecked[media._id] = checked));
    set({ checkedMediaItems: allChecked });
  },

  setEditingMedia: (media: MediaModel) => set({ editingMedia: media }),
  setSelectedMedia: (key: string, media: MediaModel | null) =>
    set((state: any) => ({
      selectedMedia: {
        ...state.selectedMedia,
        [key]: media
      }
    })),
  resetSelectedMedia: (key: string) =>
    set((state: any) => {
      const updated = { ...state.selectedMedia };
      updated[key] = null;
      return { selectedMedia: updated };
    }),
  resetAllSelectedMedia: () => set({ selectedMedia: {} }),

  resetCheckedMedia: () => set({ checkedMediaItems: {} }),
  resetEditingMedia: () => set({ editingMedia: null })
});
